import React, { useMemo, useState } from 'react';
import { getTodayDateStringInTimezone, shiftDateString } from './reminderUtils';
import {
  REMINDER_HISTORY_MAX_DAYS,
  buildReminderHistoryReport,
  downloadReminderHistoryCsv,
  downloadReminderHistoryExcel,
  downloadReminderHistoryJson,
  formatHistoryDateHeader,
  listDatesInRange,
  printReminderHistoryPdf,
} from './reminderHistoryExport';

const statusClass = (status) => {
  if (status === 'Done') return 'bg-emerald-50 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200';
  if (status === 'Pending' || status === 'Upcoming') return 'bg-amber-50 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200';
  if (status === 'Missed') return 'bg-rose-50 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200';
  return 'text-gray-400 dark:text-gray-500';
};

const ReminderHistoryReport = ({ reminders = [] }) => {
  const todayStr = getTodayDateStringInTimezone();
  const [fromDate, setFromDate] = useState(() => shiftDateString(todayStr, -6));
  const [toDate, setToDate] = useState(todayStr);
  const [applied, setApplied] = useState({ fromDate: shiftDateString(todayStr, -6), toDate: todayStr });

  const selectedDayCount = listDatesInRange(fromDate, toDate, 400).length;
  const rangeTooLong = selectedDayCount > REMINDER_HISTORY_MAX_DAYS;
  const report = useMemo(
    () => buildReminderHistoryReport(reminders, applied.fromDate, applied.toDate),
    [reminders, applied.fromDate, applied.toDate],
  );

  const handleGenerate = (e) => {
    e.preventDefault();
    if (!fromDate || !toDate) return;
    setApplied({ fromDate, toDate });
  };

  return (
    <div className="flex flex-col gap-4 min-h-0 h-full">
      <form onSubmit={handleGenerate} className="flex flex-wrap items-end gap-3">
        <label className="text-xs font-semibold text-gray-500 dark:text-gray-400">
          From
          <input
            type="date"
            max={todayStr}
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="mt-1 block rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-2.5 py-1.5 text-sm text-gray-900 dark:text-white"
          />
        </label>
        <label className="text-xs font-semibold text-gray-500 dark:text-gray-400">
          To
          <input
            type="date"
            max={todayStr}
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="mt-1 block rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-2.5 py-1.5 text-sm text-gray-900 dark:text-white"
          />
        </label>
        <button
          type="submit"
          className="px-3 py-1.5 text-sm font-semibold rounded-lg text-white bg-amber-500 hover:bg-amber-600"
        >
          Show history
        </button>
        {report.rows.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2 ml-auto">
            <button
              type="button"
              onClick={() => downloadReminderHistoryExcel(report)}
              className="px-2.5 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Excel
            </button>
            <button
              type="button"
              onClick={() => downloadReminderHistoryCsv(report)}
              className="px-2.5 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              CSV
            </button>
            <button
              type="button"
              onClick={() => printReminderHistoryPdf(report)}
              className="px-2.5 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              PDF
            </button>
            <button
              type="button"
              onClick={() => downloadReminderHistoryJson(report)}
              className="px-2.5 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              JSON
            </button>
          </div>
        ) : null}
      </form>
      {rangeTooLong ? (
        <p className="text-xs text-amber-700 dark:text-amber-300">
          Range is capped at {REMINDER_HISTORY_MAX_DAYS} days.
        </p>
      ) : null}
      <p className="text-xs text-gray-500 dark:text-gray-400">
        {formatHistoryDateHeader(report.fromDate)} – {formatHistoryDateHeader(report.toDate)}
        {' · '}
        {report.rows.length} rows
        {' · '}
        Blank cells were not scheduled that day.
      </p>
      <div className="flex-1 min-h-0 overflow-auto rounded-xl border border-gray-200 dark:border-gray-700">
        {report.rows.length === 0 ? (
          <div className="py-10 text-center text-sm text-gray-500">No reminders to show</div>
        ) : (
          <table className="min-w-full text-xs border-collapse">
            <thead className="sticky top-0 z-[1] bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="sticky left-0 z-[2] bg-gray-50 dark:bg-gray-900 text-left font-semibold text-gray-600 dark:text-gray-300 px-3 py-2 border-b border-gray-200 dark:border-gray-700 whitespace-nowrap">
                  Reminder
                </th>
                <th className="sticky left-[9rem] z-[2] bg-gray-50 dark:bg-gray-900 text-left font-semibold text-gray-600 dark:text-gray-300 px-3 py-2 border-b border-gray-200 dark:border-gray-700 whitespace-nowrap">
                  Task
                </th>
                {report.dates.map((dateStr) => (
                  <th
                    key={dateStr}
                    className="text-left font-semibold text-gray-600 dark:text-gray-300 px-2 py-2 border-b border-gray-200 dark:border-gray-700 whitespace-nowrap"
                  >
                    {formatHistoryDateHeader(dateStr)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {report.rows.map((row, index) => (
                <tr key={`${row.reminder}-${row.task}-${index}`} className="odd:bg-white even:bg-gray-50/70 dark:odd:bg-gray-800 dark:even:bg-gray-900/40">
                  <td className="sticky left-0 bg-inherit px-3 py-1.5 font-medium text-gray-900 dark:text-white whitespace-nowrap border-b border-gray-100 dark:border-gray-700">
                    {row.reminder}
                  </td>
                  <td className="sticky left-[9rem] bg-inherit px-3 py-1.5 text-gray-600 dark:text-gray-300 whitespace-nowrap border-b border-gray-100 dark:border-gray-700">
                    {row.task || '—'}
                  </td>
                  {row.cells.map((status, cellIndex) => (
                    <td
                      key={`${report.dates[cellIndex]}-${cellIndex}`}
                      className={`px-2 py-1.5 border-b border-gray-100 dark:border-gray-700 whitespace-nowrap ${statusClass(status)}`}
                    >
                      {status || '—'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ReminderHistoryReport;
